import React, { useState, useMemo, useEffect } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import { useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { useGetParametreByKeyQuery } from '../../services/coreApi';
import SaisieMatricielle from './SaisieMatricielle';
import SaisieIndividuelle from './SaisieIndividuelle';
import SaisieLigne from './SaisieLigne';
import { SectionCard } from '../../components/ui/SectionCard';

// Helper : détecte si un paramètre "verrou" est actif (true/1/oui/on).
const isVerrou = (p: any): boolean => {
  if (!p) return false;
  const v = (p.valeur_convertie ?? p.valeur ?? '').toString().toLowerCase().trim();
  return v === 'true' || v === '1' || v === 'oui' || v === 'on';
};

type ModeSaisie = 'matricielle' | 'individuelle' | 'ligne';

interface Props {
  anneeAcademique: string;
}

const SaisieRapide: React.FC<Props> = ({ anneeAcademique }) => {
  const { data: choixFormulaire, isLoading: loadingChoix } = useGetChoixFormulaireQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });

  const [mode, setMode] = useState<ModeSaisie>('matricielle');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [niveau, setNiveau] = useState<string>('');
  const [classe, setClasse] = useState<string>('');
  const [poleFilter, setPoleFilter] = useState<string>('');
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [parametresValides, setParametresValides] = useState(false);

  // Verrouillage matière : permet de garder la matière lors d'un changement
  // de classe (utile pour saisir la même matière sur plusieurs classes
  // successivement). On retient le NOM (pas l'id) car l'id change selon
  // l'option de la classe.
  const [matiereLocked, setMatiereLocked] = useState(false);
  const [lockedMatiereName, setLockedMatiereName] = useState<string | null>(null);

  // Niveaux depuis choixFormulaire
  const niveaux = useMemo(() => {
    if (!choixFormulaire?.niveaux) return [];
    return choixFormulaire.niveaux.filter(n => n.count > 0).map(n => n.code);
  }, [choixFormulaire]);

  // Classes filtrées par niveau et pôle
  const classes = useMemo(() => {
    if (!niveau) return [];
    let filtered = allClasses.filter(c => c.niveau === niveau);
    if (poleFilter) {
      filtered = filtered.filter(c => c.pole_nom === poleFilter);
    }
    return filtered.map(c => ({ code: c.code, label: `${c.code} — ${c.option_nom}`, count: 0 }));
  }, [allClasses, niveau, poleFilter]);

  // Matières filtrées STRICTEMENT par classe (utilise Option.code dérivé du code
  // de classe — évite le bug AI/AII où Classe.option_id peut être inversé).
  const { data: matieres, isLoading: loadingMatieres } = useGetMatieresByFiltersQuery(
    { classe: classe || undefined, annee_academique: anneeAcademique || undefined },
    { skip: !classe }
  );

  const selectedMatiere = Array.isArray(matieres) ? matieres.find((m) => m.id === matiereId) : undefined;

  // Quand la liste des matieres change (= classe a change), si la matiere est
  // verrouillee, on cherche une matiere homonyme dans la nouvelle classe
  useEffect(() => {
    if (!matiereLocked || !lockedMatiereName) return;
    if (!Array.isArray(matieres) || matieres.length === 0) return;
    const match = matieres.find((m: any) => m.nom_matiere === lockedMatiereName);
    if (match && match.id !== matiereId) {
      setMatiereId(match.id);
    } else if (!match && matiereId !== null) {
      // Pas de matiere homonyme dans la nouvelle classe : on garde
      // l'ancien id (sera invisible dans le selecteur, le user verra)
      setMatiereId(null);
    }
  }, [matieres, matiereLocked, lockedMatiereName]);

  const toggleMatiereLock = () => {
    if (matiereLocked) {
      setMatiereLocked(false);
      setLockedMatiereName(null);
    } else {
      // Verrouiller : memoriser le NOM de la matiere actuelle
      if (selectedMatiere) {
        setLockedMatiereName(selectedMatiere.nom_matiere);
        setMatiereLocked(true);
      }
    }
  };

  const handleValiderParametres = () => {
    if (matiereId && niveau && classe && anneeAcademique && semestre) {
      setParametresValides(true);
    }
  };

  const handleReinitialiser = () => {
    setParametresValides(false);
    setMatiereId(null);
    setNiveau('');
    setClasse('');
    setMatiereLocked(false);
    setLockedMatiereName(null);
  };

  // Semestre absolu (1..6) calculé depuis niveau (L1/L2/L3) + S1/S2.
  // L1 → S1=1, S2=2 ; L2 → S1=3, S2=4 ; L3 → S1=5, S2=6.
  const semestreAbsolu = useMemo<number | null>(() => {
    if (!niveau) return null;
    const localSem = semestre === 'S1' ? 1 : 2;
    const offset = niveau === 'L2' ? 2 : niveau === 'L3' ? 4 : 0;
    return localSem + offset;
  }, [niveau, semestre]);

  // Matières filtrées par semestre absolu (S1/S2 ciblé selon le niveau).
  // Fix 2026-10-15 : les schémas ARCHIVÉS stockent `matieres.semestre` en LOCAL
  // (1/2) alors que l'année active l'a en GLOBAL (5/6 pour L3). On tolère les
  // deux — sinon, sur une année antérieure, le filtre vidait la liste et la
  // matière sélectionnée « disparaissait » (reset par l'effet ci-dessous).
  const matieresDuSemestre = useMemo(() => {
    if (!Array.isArray(matieres)) return [];
    if (semestreAbsolu === null) return matieres;
    const localSem = semestre === 'S1' ? 1 : 2;
    return matieres.filter((m: any) => {
      const s = Number(m.semestre);
      return s === semestreAbsolu || s === localSem;
    });
  }, [matieres, semestreAbsolu, semestre]);

  // Si la matière sélectionnée n'est plus dans le semestre filtré → la dé-sélectionner
  // (sauf si la matière est verrouillée — le verrouillage est volontaire).
  useEffect(() => {
    if (matiereLocked) return;
    if (matiereId === null) return;
    const stillVisible = matieresDuSemestre.some((m: any) => m.id === matiereId);
    if (!stillVisible) setMatiereId(null);
  }, [matieresDuSemestre, matiereId, matiereLocked]);

  // Recherche dans la sidebar matières (filtrage local)
  const [matiereSearchSidebar, setMatiereSearchSidebar] = useState('');
  const matieresSidebar = useMemo(() => {
    if (!Array.isArray(matieresDuSemestre)) return [];
    if (!matiereSearchSidebar.trim()) return matieresDuSemestre;
    const q = matiereSearchSidebar.toLowerCase();
    return matieresDuSemestre.filter((m: any) =>
      (m.nom_matiere || '').toLowerCase().includes(q) ||
      (m.code_matiere || '').toLowerCase().includes(q)
    );
  }, [matieresDuSemestre, matiereSearchSidebar]);

  return (
    <div className={`${parametresValides ? 'max-w-screen-2xl' : 'max-w-7xl'} mx-auto`}>
      {!parametresValides ? (
        <SectionCard
          title="Configuration"
          subtitle="Sélectionnez le niveau, la classe, la matière et le semestre"
          padding="p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Pôle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pôle
              </label>
              <select
                value={poleFilter}
                onChange={(e) => {
                  setPoleFilter(e.target.value);
                  setClasse('');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Tous les pôles</option>
                {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Niveau */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Niveau <span className="text-red-500">*</span>
              </label>
              <select
                value={niveau}
                onChange={(e) => {
                  setNiveau(e.target.value);
                  setClasse('');
                  setMatiereId(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">-- Sélectionner --</option>
                {niveaux.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Classe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Classe <span className="text-red-500">*</span>
              </label>
              <select
                value={classe}
                onChange={(e) => {
                  setClasse(e.target.value);
                  setMatiereId(null);
                }}
                disabled={!niveau}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">-- Sélectionner --</option>
                {classes.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Matière */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Matière <span className="text-red-500">*</span>
              </label>
              {loadingMatieres || loadingChoix ? (
                <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-300">
                  Chargement...
                </div>
              ) : (
                <select
                  value={matiereId || ''}
                  onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!classe}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {(matieres || []).map((matiere) => (
                    <option key={matiere.id} value={matiere.id}>
                      {matiere.code_matiere} - {matiere.nom_matiere}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Semestre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Semestre <span className="text-red-500">*</span>
              </label>
              <select
                value={semestre}
                onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="S1">Semestre 1</option>
                <option value="S2">Semestre 2</option>
              </select>
            </div>

            {/* Année */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Année académique
              </label>
              <input
                type="text"
                value={anneeAcademique}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleValiderParametres}
              disabled={!matiereId || !niveau || !classe}
              className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Valider et continuer
            </button>
          </div>
        </SectionCard>
      ) : (
        <>
          {/* Barre contextuelle compacte avec SÉLECTEURS RAPIDES (sticky) */}
          <div className="sticky top-0 z-20 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-2 mb-4 shadow-sm">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Classe */}
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase">Classe</label>
                <select
                  value={classe}
                  onChange={(e) => {
                    setClasse(e.target.value);
                    // Si la matiere n'est PAS verrouillee, on reset (comportement
                    // d'origine). Si verrouillee, le useEffect cherchera une
                    // matiere homonyme dans la nouvelle classe.
                    if (!matiereLocked) setMatiereId(null);
                  }}
                  className="px-2 py-1 text-xs font-bold bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {classes.map(c => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>

              {/* Semestre + indicateur clôture (fix 2026-08-27) */}
              <SemestreSelector
                value={semestre}
                onChange={setSemestre}
              />

              <span className="text-[10px] text-blue-700 dark:text-blue-300">{anneeAcademique}</span>

              {/* Matière */}
              {matieresDuSemestre.length > 0 && (
                <div className="flex items-center gap-1.5 flex-1 min-w-[200px] border-l border-blue-300 dark:border-blue-700 pl-2 ml-1">
                  <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase whitespace-nowrap">Matière</label>
                  <select
                    value={matiereId || ''}
                    onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
                    className="flex-1 min-w-0 px-2 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 rounded truncate focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Choisir —</option>
                    {matieresDuSemestre.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.code_matiere} — {m.nom_matiere}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={toggleMatiereLock}
                    disabled={!matiereLocked && !matiereId}
                    title={matiereLocked
                      ? `Déverrouiller (matière fixée : ${lockedMatiereName})`
                      : 'Verrouiller la matière (conservée lors du changement de classe)'}
                    className={`p-1 rounded transition-colors ${
                      matiereLocked
                        ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-600'
                        : 'text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                    }`}
                  >
                    {matiereLocked ? <Lock size={14} /> : <Unlock size={14} />}
                  </button>
                  {matiereLocked && (
                    <span className="text-[10px] text-amber-700 dark:text-amber-300 whitespace-nowrap">
                      verrouillée
                    </span>
                  )}
                </div>
              )}

              <button
                onClick={handleReinitialiser}
                className="text-[10px] text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 underline whitespace-nowrap ml-auto"
                title="Réinitialiser tous les paramètres"
              >
                ↻ Reset
              </button>
            </div>
          </div>

          {/* Layout 2 colonnes : sidebar matières + zone saisie */}
          <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-3">
            {/* ─── SIDEBAR MATIÈRES ─── */}
            <aside className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden self-start lg:sticky lg:top-20">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center justify-between">
                  Matières {classe} · {semestre}
                  <span className="text-xs font-normal text-gray-500 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded"
                    title={`${matieresDuSemestre.length} matières du ${semestre} (sur ${(matieres || []).length} au total dans la classe)`}>
                    {matieresDuSemestre.length}
                    {(matieres || []).length !== matieresDuSemestre.length && (
                      <span className="text-gray-400">/{(matieres || []).length}</span>
                    )}
                  </span>
                </h3>
                <input
                  type="text"
                  value={matiereSearchSidebar}
                  onChange={(e) => setMatiereSearchSidebar(e.target.value)}
                  placeholder="Rechercher une matière..."
                  className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                {loadingMatieres ? (
                  <div className="p-4 text-center text-xs text-gray-400">Chargement…</div>
                ) : matieresSidebar.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-400">
                    {matiereSearchSidebar ? 'Aucune matière trouvée' : 'Aucune matière'}
                  </div>
                ) : (
                  matieresSidebar.map((m: any) => {
                    const active = m.id === matiereId;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setMatiereId(m.id)}
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 transition-colors ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 border-l-4 border-l-transparent'
                        }`}
                      >
                        <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400">
                          {m.code_matiere}
                        </div>
                        <div className={`text-sm font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>
                          {m.nom_matiere}
                        </div>
                        {(m.enseignant_nom || m.enseignant) && (
                          <div className="text-[11px] text-gray-400 truncate">
                            {m.enseignant_nom || m.enseignant}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* ─── ZONE SAISIE ─── */}
            <div>
              {/* Fix 2026-08-27 : bandeau "Matière active" supprimé (redondant
                  avec le select MATIÈRE au-dessus). Les modes 1/2/3 sont
                  fusionnés en une ligne compacte. */}
              <div className="mb-2">
                <nav className="flex gap-1 flex-wrap" aria-label="Modes de saisie">
                  <button
                    onClick={() => setMode('matricielle')}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      mode === 'matricielle'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                    }`}
                    title="Mode 1 : tableau matriciel toute la classe"
                  >
                    Mode 1 : Matriciel
                  </button>
                  <button
                    onClick={() => setMode('individuelle')}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      mode === 'individuelle'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                    }`}
                    title="Mode 2 : fiche par étudiant"
                  >
                    Mode 2 : Fiche
                  </button>
                  <button
                    onClick={() => setMode('ligne')}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      mode === 'ligne'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                    }`}
                    title="Mode 3 : ligne par ligne via matricule scannable"
                  >
                    Mode 3 : Ligne
                  </button>
                </nav>
              </div>

              {/* Composants de saisie */}
              <div>
                {mode === 'matricielle' && matiereId && classe && (
                  <SaisieMatricielle
                    classe={classe}
                    matiereId={matiereId}
                    matiereLabel={selectedMatiere ? `${selectedMatiere.code_matiere} — ${selectedMatiere.nom_matiere}` : undefined}
                    anneeAcademique={anneeAcademique}
                    semestre={semestre}
                  />
                )}
                {mode === 'individuelle' && matiereId && (
                  <SaisieIndividuelle
                    matiereId={matiereId}
                    matiereLabel={selectedMatiere ? `${selectedMatiere.code_matiere} — ${selectedMatiere.nom_matiere}` : undefined}
                    anneeAcademique={anneeAcademique}
                    semestre={semestre}
                    classe={classe}
                  />
                )}
                {mode === 'ligne' && matiereId && (
                  <SaisieLigne
                    matiereId={matiereId}
                    matiereLabel={selectedMatiere ? `${selectedMatiere.code_matiere} — ${selectedMatiere.nom_matiere}` : undefined}
                    anneeAcademique={anneeAcademique}
                    semestre={semestre}
                    classe={classe}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Sélecteur Semestre + indicateur clôture (fix 2026-08-27) ────────────────
// Affiche un cadenas 🔒 rouge à côté de la valeur si le semestre est verrouillé
// pour la saisie (paramètre admin). L'agent voit immédiatement qu'il ne pourra
// pas enregistrer et peut basculer sur l'autre semestre.
const SemestreSelector: React.FC<{
  value: 'S1' | 'S2';
  onChange: (v: 'S1' | 'S2') => void;
}> = ({ value, onChange }) => {
  const { data: pGlobal } = useGetParametreByKeyQuery('saisie_notes_verrouillee');
  const { data: pS1 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1');
  const { data: pS2 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2');
  const { data: pS1Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ord');
  const { data: pS2Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ord');

  const global = isVerrou(pGlobal);
  const s1Cloture = global || isVerrou(pS1) || isVerrou(pS1Ord);
  const s2Cloture = global || isVerrou(pS2) || isVerrou(pS2Ord);
  const currentCloture = value === 'S1' ? s1Cloture : s2Cloture;

  return (
    <div className="flex items-center gap-1.5">
      <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase">Sem.</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as 'S1' | 'S2')}
        className={`px-2 py-1 text-xs bg-white dark:bg-gray-700 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          currentCloture
            ? 'border-red-400 text-red-700 dark:text-red-300 dark:border-red-700 font-semibold'
            : 'border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100'
        }`}
        title={currentCloture
          ? `⚠ ${value} est verrouillé pour la saisie — vos modifications seront refusées`
          : 'Semestre'}
      >
        <option value="S1">S1{s1Cloture ? ' 🔒' : ''}</option>
        <option value="S2">S2{s2Cloture ? ' 🔒' : ''}</option>
      </select>
      {currentCloture && (
        <span className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase whitespace-nowrap">
          🔒 clôturé
        </span>
      )}
    </div>
  );
};

export default SaisieRapide;
